import type { ContentfulModule } from 'libs/contentful/types';
import type { ResolvedModule, SelectedPersonalization } from './types';

/**
 * Narrows a ContentfulComponent or arbitrary unknown to ContentfulModule.
 *
 * PageProps fields (hero, modulesCollection, etc.) are typed as specific prop
 * unions that extend ContentfulComponent but not ContentfulModule. At runtime
 * these objects carry all the fields the experiment system needs (sys, __typename,
 * experiment refs). This guard bridges the two type hierarchies.
 *
 * The `unknown` overload is used by the resolver when narrowing entries pulled
 * from `_includesMap` or from a container's `modulesCollection.items` (whose
 * elements are typed as `unknown[]` by `hasModulesCollection`).
 */
export function isContentfulModule(entry: unknown): entry is ContentfulModule {
  if (typeof entry !== 'object' || entry === null) return false;
  if (!('sys' in entry) || typeof entry.sys !== 'object' || entry.sys === null) return false;
  if (!('id' in entry.sys) || typeof entry.sys.id !== 'string') return false;
  if (!('__typename' in entry) || typeof entry.__typename !== 'string') return false;
  return true;
}

/**
 * Checks if a module participates in at least one Statsig A/B test via the
 * `statsigExperiments` / `statsigExperimentsCollection` array.
 *
 * The narrowed type is intentionally conservative — callers should use
 * `getStatsigContainerItems` to read the full set of attached containers in
 * a shape-agnostic way.
 */
export function hasStatsigContainer(module: unknown): module is ContentfulModule {
  return getStatsigContainerItems(module).length > 0;
}

/**
 * Shape returned by `getStatsigContainerItems`: a Statsig variant container
 * that has been runtime-validated to carry a string `sys.id`. The remaining
 * fields are arbitrary because containers come from Contentful (REST or
 * GraphQL) and carry different shapes (e.g. `treatmentVariations` vs
 * `treatmentVariationsCollection`).
 */
export type StatsigContainerLike = { sys: { id: string } } & Record<string, unknown>;

/**
 * Reads every Statsig variant container attached to a module, handling both
 * GraphQL and REST shapes:
 *   - GraphQL: `statsigExperimentsCollection.items`
 *   - REST (with preserveExperimentFields): `statsigExperiments` (raw array
 *     or `{items}`)
 *
 * Items that don't carry a string `sys.id` are filtered out (the resolver
 * needs the id to look up `selection.experienceId`). Order is preserved —
 * the resolver picks the first matching selection by Contentful array order,
 * which is the documented priority rule for multi-experiment modules.
 */
export function getStatsigContainerItems(module: unknown): StatsigContainerLike[] {
  if (typeof module !== 'object' || module === null) return [];
  const obj = module as Record<string, unknown>;

  // `readCollection` may return the input's own `items` array by reference, so
  // pick the first non-empty source via assignment instead of pushing into it
  // — mutating the caller's input would surprise downstream code (e.g.
  // `hasStatsigContainer` runs first, then `getExperimentIds` reads the same
  // object).
  const fromGraphQL = readCollection(obj.statsigExperimentsCollection);
  const fromRest = Array.isArray(obj.statsigExperiments)
    ? obj.statsigExperiments
    : readCollection(obj.statsigExperiments);
  const items = fromGraphQL.length > 0 ? fromGraphQL : fromRest;

  const seen = new Set<string>();
  const result: StatsigContainerLike[] = [];
  for (const item of items) {
    if (!isStatsigContainerLike(item)) continue;
    if (seen.has(item.sys.id)) continue;
    seen.add(item.sys.id);
    result.push(item);
  }
  return result;
}

function isStatsigContainerLike(item: unknown): item is StatsigContainerLike {
  if (typeof item !== 'object' || item === null) return false;
  const obj = item as Record<string, unknown>;
  const sys = obj.sys;
  if (typeof sys !== 'object' || sys === null) return false;
  const id = (sys as Record<string, unknown>).id;
  return typeof id === 'string';
}

/**
 * Reads items from a GraphQL collection or REST array shape.
 *   - GraphQL: `{items: [...]}`
 *   - REST: `[...]` (array directly) or `{items: [...]}`
 */
export function readCollection(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') {
    const items = (value as Record<string, unknown>).items;
    if (Array.isArray(items)) return items;
  }
  return [];
}

/**
 * Reads Ninetailed experience items from a module, handling all data shapes:
 *   - GraphQL: `ntExperiencesCollection.items`
 *   - REST (with preserveExperimentFields): `nt_experiences` (array or `{items}`)
 *   - REST snake-case collection: `nt_experiencesCollection.items`
 *   - Raw Contentful Entry: `fields.nt_experiences` (array or `{items}`)
 */
export function getNinetailedExperienceItems(module: unknown): unknown[] {
  if (typeof module !== 'object' || module === null) return [];
  const obj = module as Record<string, unknown>;

  const camel = readCollection(obj.ntExperiencesCollection);
  if (camel.length > 0) return camel;

  const snakeCollection = readCollection(obj.nt_experiencesCollection);
  if (snakeCollection.length > 0) return snakeCollection;

  const snake = readCollection(obj.nt_experiences);
  if (snake.length > 0) return snake;

  const fields = obj.fields;
  if (fields && typeof fields === 'object' && !Array.isArray(fields)) {
    return readCollection((fields as Record<string, unknown>).nt_experiences);
  }

  return [];
}

/**
 * Reads variant items from a Ninetailed experience entry, handling both data shapes:
 *   - GraphQL: `ntVariantsCollection.items`
 *   - REST (with preserveExperimentFields): `nt_variants` (array)
 */
export function getNinetailedVariantItems(experience: unknown): unknown[] {
  if (typeof experience !== 'object' || experience === null) return [];
  const obj = experience as Record<string, unknown>;

  const camel = readCollection(obj.ntVariantsCollection);
  if (camel.length > 0) return camel;

  return readCollection(obj.nt_variants);
}

/**
 * Checks if a module has Ninetailed experiences attached,
 * indicating it participates in a Ninetailed experiment or personalization.
 */
export function hasNinetailedExperiences(module: unknown): module is ContentfulModule {
  return getNinetailedExperienceItems(module).length > 0;
}

/**
 * Checks if an entry has a `modulesCollection` field, identifying it as
 * a container entry like PageSection that holds child modules.
 */
export function hasModulesCollection(entry: unknown): entry is { modulesCollection: { items: unknown[] } } {
  if (typeof entry !== 'object' || entry === null) return false;
  const obj = entry as Record<string, unknown>;
  const collection = obj.modulesCollection;
  if (typeof collection !== 'object' || collection === null) return false;
  return Array.isArray((collection as Record<string, unknown>).items);
}

export function isPersonalizedEntry(entry: unknown): entry is ResolvedModule & { _exposure: SelectedPersonalization } {
  if (typeof entry !== 'object' || entry === null) return false;
  const obj = entry as Record<string, unknown>;
  const exposure = obj._exposure;
  if (typeof exposure !== 'object' || exposure === null) return false;
  const exp = exposure as Record<string, unknown>;
  return (
    typeof exp.experienceId === 'string' &&
    typeof exp.variantIndex === 'number' &&
    typeof exp.variants === 'object' &&
    exp.variants !== null
  );
}

/**
 * Narrows a Ninetailed SDK component baseline to entry-form (`{ id }`,
 * EntryReplacement), excluding inline-form (`{ value }`, InlineVariable).
 */
export function isEntryBaseline(baseline: unknown): baseline is { id: string } {
  return baseline != null && typeof baseline === 'object' && 'id' in baseline && typeof baseline.id === 'string';
}

/**
 * Narrows a Ninetailed SDK component variant to entry-form (`{ id, internalName? }`),
 * excluding inline-form variants (`{ value }`).
 */
export function isEntryVariant(variant: unknown): variant is { id: string; internalName?: string } {
  if (variant == null || typeof variant !== 'object' || !('id' in variant) || typeof variant.id !== 'string') {
    return false;
  }
  return !('internalName' in variant) || typeof variant.internalName === 'string' || variant.internalName == null;
}
